import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { QuickLeadForm } from "@/components/forms/QuickLeadForm";
import { LeadMagnet } from "@/components/LeadMagnet";
import { ArrowRight, Clock, BookOpen, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { GUIDE_CATEGORIES } from "@/data/site";

export default function GuideHub() {
  const [cat, setCat] = useState(null);
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState(null);

  const { data: guides = [], isLoading } = useQuery({
    queryKey: ["guides-all"],
    queryFn: () => api.get("/guides").then((r) => r.data),
  });

  const topTags = useMemo(() => {
    const counts = {};
    guides.forEach((g) => (g.tags || []).forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 16).map(([t]) => t);
  }, [guides]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return guides.filter((g) => {
      if (cat && g.category !== cat) return false;
      if (tag && !(g.tags || []).includes(tag)) return false;
      if (q) {
        const hay = `${g.title} ${g.excerpt} ${(g.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [guides, cat, tag, query]);

  const clearAll = () => { setCat(null); setTag(null); setQuery(""); };
  const active = cat || tag || query;

  return (
    <>
      <Seo title="Centro Mobilità Senior — Guide e approfondimenti"
        description="Il Centro Mobilità Senior di SENIORCAR: guide, confronti e approfondimenti su mobilità per persone con difficoltà motorie, dispositivi medici, agevolazioni e sicurezza."
        keywords="guide mobilità senior, dispositivo medico mobilità, scooter per anziani" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6"><Breadcrumbs items={[{ label: "Guide" }]} /></div>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <span className="uppercase text-xs font-bold tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full flex items-center gap-1.5 w-fit"><BookOpen size={14} /> Centro Mobilità Senior</span>
        <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>Guide alla mobilità senior</h1>
        <p className="mt-4 text-lg sm:text-xl text-slate-600 max-w-2xl">Contenuti chiari e affidabili per scegliere con serenità la soluzione di mobilità giusta.</p>

        <div className="mt-6 relative max-w-xl">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca tra le guide (es. autonomia, Legge 104, montascale...)"
            className="w-full h-12 pl-11 pr-10 rounded-full border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-600"
            data-testid="guide-search-input"
          />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="guide-search-clear">
              <X size={18} />
            </button>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-2 flex-wrap mb-4" data-testid="guide-categories">
          <button onClick={() => setCat(null)} className={`px-4 py-2 rounded-full text-sm font-semibold border ${!cat ? "text-white border-transparent" : "text-slate-700 border-slate-300 bg-white"}`} style={!cat ? { backgroundColor: "var(--sc-emerald)" } : {}}>Tutte</button>
          {GUIDE_CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(cat === c ? null : c)} className={`px-4 py-2 rounded-full text-sm font-semibold border ${cat === c ? "text-white border-transparent" : "text-slate-700 border-slate-300 bg-white"}`} style={cat === c ? { backgroundColor: "var(--sc-emerald)" } : {}} data-testid={`cat-${c}`}>{c}</button>
          ))}
        </div>

        {topTags.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-6 items-center" data-testid="guide-tags">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400 mr-1">Argomenti:</span>
            {topTags.map((t) => (
              <button key={t} onClick={() => setTag(tag === t ? null : t)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${tag === t ? "bg-amber-500 text-white border-transparent" : "text-slate-600 border-slate-200 bg-slate-50 hover:border-amber-400"}`}
                data-testid={`tag-${t}`}>#{t}</button>
            ))}
          </div>
        )}

        {active && (
          <div className="mb-6 flex items-center gap-3 text-sm">
            <span className="text-slate-500">{filtered.length} risultati</span>
            <button onClick={clearAll} className="inline-flex items-center gap-1 text-emerald-800 font-semibold hover:underline" data-testid="guide-clear-filters"><X size={14} /> Azzera filtri</button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {isLoading ? <p className="text-slate-500">Caricamento...</p> : (
              <div className="grid sm:grid-cols-2 gap-5">
                {filtered.length === 0 && <p className="text-slate-500 col-span-2" data-testid="guide-no-results">Nessuna guida trovata. Prova a modificare la ricerca o i filtri.</p>}
                {filtered.map((g) => (
                  <Link key={g.slug} to={`/guide/${g.slug}`} className="rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-md hover:-translate-y-1 transition-all" data-testid={`guide-card-${g.slug}`}>
                    <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">{g.category}</span>
                    <h2 className="mt-2 text-lg font-bold text-slate-900 leading-snug" style={{ fontFamily: "Outfit" }}>{g.title}</h2>
                    <p className="mt-2 text-slate-600 text-sm line-clamp-3">{g.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <span className="text-slate-500 flex items-center gap-1"><Clock size={14} /> {g.reading_time} min</span>
                      <span className="text-emerald-800 font-semibold flex items-center gap-1">Leggi <ArrowRight size={14} /></span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <aside className="space-y-6">
            <LeadMagnet compact />
            <QuickLeadForm compact />
            <Link to="/offerta" className="block rounded-2xl p-6 text-white" style={{ backgroundColor: "var(--sc-emerald)" }}>
              <h3 className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>Ordina la tua SENIORCAR</h3>
              <p className="text-emerald-100/80 text-sm mt-1">Conferma l'ordine online e accedi all'offerta dedicata.</p>
              <Button className="mt-4 h-11 font-bold text-white rounded-full w-full" style={{ backgroundColor: "var(--sc-amber)" }}>Prenota Online</Button>
            </Link>
          </aside>
        </div>
      </section>
    </>
  );
}
